import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from "react";

const AccessibilityContext = createContext();

export const useAccessibility = () => useContext(AccessibilityContext);

// Klucze localStorage
const FONT_SCALE_KEY = "a11y-font-scale";        // legacy (indeksy 0-3)
const FONT_SCALE_KEY_V2 = "a11y-font-scale-v2";  // nowa skala 0-5 (dodane 2 mniejsze stopnie)
const HIGH_CONTRAST_KEY = "a11y-high-contrast";
const READER_MODE_KEY = "a11y-reader-mode";

/* BAZOWA SKALA INTERFEJSU — musi być równa `html { font-size }` z styles/global.css.
   Ten kontekst ustawia rozmiar czcionki <html> stylem INLINE, więc zawsze wygrywa
   z arkuszem; gdyby obie liczby się rozjechały, aplikacja skakałaby na rozmiarze
   w chwili montowania Reacta. Zmieniasz jedną — zmień drugą. */
export const UI_BASE_SCALE_PERCENT = 87.5;

/* Skala czcionki — zmienia html font-size, co skaluje wszystkie wartości rem.
   Dodane 2 MNIEJSZE stopnie (turbo małe) z przodu; domyślny to index 3 ("A" = 1.0x).

   ZMIANA (kampania 1920x1080): stopnie były podawane w PIKSELACH (htmlPx), co
   bezpowrotnie nadpisywało rozmiar czcionki ustawiony przez użytkownika w
   przeglądarce — dla osoby, która podniosła go z powodu wzroku, przyciski A−/A/A+
   były jedynym ratunkiem, a i one dawały sztywne piksele (WCAG 1.4.4).
   Teraz stopień to PROCENT bazy przeglądarki: mnożnik `value` × UI_BASE_SCALE_PERCENT.
   Krok skali („A+ jest o 12.5% większe od A") jest identyczny jak dotąd, ale
   ustawienie przeglądarki znów jest respektowane. */
const FONT_SCALES = [
    { label: "A⁻⁻⁻", value: 0.75 },      // 75%  (najmniejszy)
    { label: "A⁻⁻",  value: 0.8125 },    // 81.25%
    { label: "A⁻",   value: 0.875 },     // 87.5%
    { label: "A",    value: 1.0 },       // 100% (domyślne = UI_BASE_SCALE_PERCENT)
    { label: "A⁺",   value: 1.125 },     // 112.5%
    { label: "AA",   value: 1.25 },      // 125%
];

// Procent, jaki trafia na <html> dla danego stopnia skali.
const scaleToPercent = (value) => parseFloat((UI_BASE_SCALE_PERCENT * value).toFixed(4));

const DEFAULT_SCALE_INDEX = 3; // "A" = 1.0x (po dodaniu 2 mniejszych stopni z przodu)

// Odczyt + jednorazowa migracja starego klucza (0-3) na nową skalę (0-5): nowy = stary + 2.
const readInitialScaleIndex = () => {
    try {
        const v2 = localStorage.getItem(FONT_SCALE_KEY_V2);
        if (v2 !== null) {
            const idx = parseInt(v2, 10);
            return idx >= 0 && idx < FONT_SCALES.length ? idx : DEFAULT_SCALE_INDEX;
        }
        const legacy = localStorage.getItem(FONT_SCALE_KEY);
        if (legacy !== null) {
            const old = parseInt(legacy, 10);
            return (old >= 0 && old <= 3) ? old + 2 : DEFAULT_SCALE_INDEX; // 0..3 -> 2..5
        }
    } catch { /* localStorage niedostępny */ }
    return DEFAULT_SCALE_INDEX;
};

export const AccessibilityProvider = ({ children }) => {
    // --- Rozmiar czcionki ---
    const [fontScaleIndex, setFontScaleIndex] = useState(readInitialScaleIndex);

    // --- Wysoki kontrast ---
    const [highContrast, setHighContrast] = useState(() => {
        return localStorage.getItem(HIGH_CONTRAST_KEY) === "true";
    });

    // --- Tryb czytnika ---
    const [readerMode, setReaderMode] = useState(() => {
        return localStorage.getItem(READER_MODE_KEY) === "true";
    });

    // Zastosuj skalę czcionki na <html>
    useEffect(() => {
        const scale = FONT_SCALES[fontScaleIndex];
        document.documentElement.style.fontSize = `${scaleToPercent(scale.value)}%`;
        document.documentElement.setAttribute("data-font-scale", String(fontScaleIndex));
        localStorage.setItem(FONT_SCALE_KEY_V2, String(fontScaleIndex));
    }, [fontScaleIndex]);

    // Zastosuj atrybut high contrast
    useEffect(() => {
        if (highContrast) {
            document.documentElement.setAttribute("data-high-contrast", "true");
        } else {
            document.documentElement.removeAttribute("data-high-contrast");
        }
        localStorage.setItem(HIGH_CONTRAST_KEY, String(highContrast));
    }, [highContrast]);

    // Zastosuj atrybut reader mode
    useEffect(() => {
        if (readerMode) {
            document.documentElement.setAttribute("data-reader-mode", "true");
        } else {
            document.documentElement.removeAttribute("data-reader-mode");
        }
        localStorage.setItem(READER_MODE_KEY, String(readerMode));
    }, [readerMode]);

    // Akcje
    const increaseFontScale = useCallback(() => {
        setFontScaleIndex(prev => Math.min(prev + 1, FONT_SCALES.length - 1));
    }, []);

    const decreaseFontScale = useCallback(() => {
        setFontScaleIndex(prev => Math.max(prev - 1, 0));
    }, []);

    const resetFontScale = useCallback(() => {
        setFontScaleIndex(DEFAULT_SCALE_INDEX);
    }, []);

    const toggleHighContrast = useCallback(() => {
        setHighContrast(prev => !prev);
    }, []);

    const toggleReaderMode = useCallback(() => {
        setReaderMode(prev => !prev);
    }, []);

    const resetAll = useCallback(() => {
        setFontScaleIndex(DEFAULT_SCALE_INDEX);
        setHighContrast(false);
        setReaderMode(false);
    }, []);

    const value = useMemo(() => ({
        // Stan
        fontScaleIndex,
        fontScaleLabel: FONT_SCALES[fontScaleIndex].label,
        fontScaleValue: FONT_SCALES[fontScaleIndex].value,
        highContrast,
        readerMode,
        canIncrease: fontScaleIndex < FONT_SCALES.length - 1,
        canDecrease: fontScaleIndex > 0,
        isDefault: fontScaleIndex === DEFAULT_SCALE_INDEX && !highContrast && !readerMode,
        // Akcje
        increaseFontScale,
        decreaseFontScale,
        resetFontScale,
        toggleHighContrast,
        toggleReaderMode,
        resetAll,
    }), [
        fontScaleIndex, highContrast, readerMode,
        increaseFontScale, decreaseFontScale, resetFontScale,
        toggleHighContrast, toggleReaderMode, resetAll,
    ]);

    return (
        <AccessibilityContext.Provider value={value}>
            {children}
        </AccessibilityContext.Provider>
    );
};

export default AccessibilityContext;
